import { ParsedViewData } from '@/utils/svgPathParser';
import { VisibleEdges, HiddenEdges } from './edges/VisibleEdges';
import { SectionHatching } from './SectionHatching';
import { CenterLines } from './CenterLines';
import { DimensionGroup } from './DimensionGroup';
import { SectionCutLine } from './SectionCutLine';
import { AnnotationLayer } from './AnnotationLayer';
import { COLORS, DRAWING_COLORS, LINE_STYLES, type RenderMode } from './constants';
import { SmartDimensionGroup } from './SmartDimension';
import { Parameter, SectionPlane, DrawingAnnotation, DimensionSpec } from '@shared/types';
import { Loader2, CircleAlert } from 'lucide-react';

type ProjectionViewportProps = {
  view: string;
  x: number;
  y: number;
  width: number;
  height: number;
  parsedData: ParsedViewData | null;
  isCompiling: boolean;
  error: Error | null;
  parameters?: Parameter[];
  renderMode?: RenderMode;
  sectionPlanes?: SectionPlane[];
  annotations?: DrawingAnnotation[];
  dimensions?: DimensionSpec[];
  onParameterChange?: (param: Parameter, value: number) => void;
  onParameterCommit?: (param: Parameter, value: number) => void;
};

const VIEW_LABELS: Record<string, string> = {
  top: '平面図',
  front: '正面図',
  right: '右側面図',
  isometric: 'アイソメ図',
  rear: '背面図',
  left: '左側面図',
  bottom: '底面図',
};

export function ProjectionViewport({
  view,
  x,
  y,
  width,
  height,
  parsedData,
  isCompiling,
  error,
  parameters = [],
  renderMode,
  sectionPlanes,
  annotations,
  dimensions,
  onParameterChange,
  onParameterCommit,
}: ProjectionViewportProps) {
  const isDrawingMode = renderMode === 'drawing';
  const colors = isDrawingMode ? DRAWING_COLORS : COLORS;
  const label = VIEW_LABELS[view] ?? (view.startsWith('section-') ? `${view.replace('section-', '')} 断面図` : view);
  const padding = 8;

  // Show dimensions on front, top, and right views
  const showDimensions = (view === 'front' || view === 'top' || view === 'right') && parameters.length > 0;

  const clipId = `viewport-clip-${view}`;

  return (
    <g className={`viewport-${view}`} transform={`translate(${x}, ${y})`}>
      <defs>
        <clipPath id={clipId}>
          <rect x={-1} y={-1} width={width + 2} height={height + 2} />
        </clipPath>
      </defs>

      {/* Viewport border */}
      <rect
        x={0}
        y={0}
        width={width}
        height={height}
        fill="none"
        stroke={colors.border}
        strokeWidth={LINE_STYLES.dimension.strokeWidth}
        strokeDasharray="2,2"
        opacity={0.3}
      />

      {/* View label */}
      <text
        x={padding}
        y={padding + 4}
        dominantBaseline="hanging"
        fill={colors.text}
        fontSize={4.5}
        fontWeight="700"
        fontFamily="'Noto Sans JP', sans-serif"
      >
        {label}
      </text>

      {/* Content area - clipped to viewport */}
      <g clipPath={`url(#${clipId})`}>
      {isCompiling ? (
        <foreignObject x={0} y={0} width={width} height={height}>
          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Loader2 style={{ width: 16, height: 16, color: '#170C79' }} className="animate-spin" />
          </div>
        </foreignObject>
      ) : error ? (
        <foreignObject x={0} y={0} width={width} height={height}>
          <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
            <CircleAlert style={{ width: 14, height: 14, color: '#170C79' }} />
            <span style={{ fontSize: 8, color: 'rgba(23, 12, 121, 0.6)' }}>失敗</span>
          </div>
        </foreignObject>
      ) : parsedData ? (
        <g>
          <ViewContent
            view={view}
            parsedData={parsedData}
            vpWidth={width}
            vpHeight={height}
            padding={padding + 6}
            showDimensions={showDimensions}
            parameters={parameters}
            renderMode={renderMode}
            sectionPlanes={sectionPlanes}
            annotations={annotations}
            dimensions={dimensions ?? []}
            onParameterChange={onParameterChange}
            onParameterCommit={onParameterCommit}
          />
        </g>
      ) : (
        <text
          x={width / 2}
          y={height / 2}
          textAnchor="middle"
          dominantBaseline="central"
          fill={colors.titleBlock}
          fontSize={3}
          opacity={0.4}
        >
          データなし
        </text>
      )}
      </g>
    </g>
  );
}

function ViewContent({
  view,
  parsedData,
  vpWidth,
  vpHeight,
  padding,
  showDimensions,
  parameters,
  renderMode,
  sectionPlanes,
  annotations,
  dimensions,
  onParameterChange,
  onParameterCommit,
}: {
  view: string;
  parsedData: ParsedViewData;
  vpWidth: number;
  vpHeight: number;
  padding: number;
  showDimensions: boolean;
  parameters: Parameter[];
  renderMode?: RenderMode;
  sectionPlanes?: SectionPlane[];
  annotations?: DrawingAnnotation[];
  dimensions?: DimensionSpec[];
  onParameterChange?: (param: Parameter, value: number) => void;
  onParameterCommit?: (param: Parameter, value: number) => void;
}) {
  const { viewBox: svgVb, paths } = parsedData;

  // Calculate scale to fit within viewport with padding
  const availW = vpWidth - padding * 2;
  const availH = vpHeight - padding * 2;
  const scaleX = availW / svgVb.width;
  const scaleY = availH / svgVb.height;
  const scale = Math.min(scaleX, scaleY, 1);

  const scaledW = svgVb.width * scale;
  const scaledH = svgVb.height * scale;
  const offsetX = padding + (availW - scaledW) / 2;
  const offsetY = padding + (availH - scaledH) / 2;

  // Filter section planes that apply to this view
  const viewSectionPlanes = sectionPlanes?.filter((p) => {
    // Show section lines on orthographic views (not on section views themselves)
    return !view.startsWith('section-') && view !== 'isometric';
  });

  return (
    <g transform={`translate(${offsetX}, ${offsetY}) scale(${scale})`}>
      <g transform={`translate(${-svgVb.minX}, ${-svgVb.minY})`}>
        <SectionHatching paths={paths} />
        <HiddenEdges paths={paths} />
        <VisibleEdges paths={paths} />
        <CenterLines viewBox={svgVb} />

        {/* Section cut lines */}
        {viewSectionPlanes?.map((plane, i) => (
          <SectionCutLine
            key={`section-${plane.label || i}`}
            plane={plane}
            viewBox={svgVb}
            vpWidth={svgVb.width}
            vpHeight={svgVb.height}
          />
        ))}

        {/* Annotations */}
        {annotations && annotations.length > 0 && (
          <AnnotationLayer
            annotations={annotations}
            viewType={view}
            viewBox={svgVb}
            vpWidth={svgVb.width}
            vpHeight={svgVb.height}
          />
        )}

        {/* AI-positioned smart dimensions (drawing mode) */}
        {dimensions && dimensions.length > 0 && renderMode === 'drawing' && (
          <SmartDimensionGroup
            dimensions={dimensions}
            viewType={view}
            viewBox={svgVb}
          />
        )}

        {/* Interactive parameter-based dimensions (fallback) */}
        {showDimensions && !(dimensions && dimensions.length > 0 && renderMode === 'drawing') && (
          <DimensionGroup
            view={view as any}
            viewBox={svgVb}
            parameters={parameters}
            renderMode={renderMode}
            onParameterChange={onParameterChange}
            onParameterCommit={onParameterCommit}
          />
        )}
      </g>
    </g>
  );
}
